import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { PageHead, Panel, F, In, Sel, Btn, Pill } from "@/components/admin/kit";
import { Loading } from "@/components/site/Primitives";

const AddUser = ({ onAdded }) => {
  const [f, setF] = useState({ email: "", name: "", role: "staff", password: "" });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const add = async () => {
    try { await api.post("/admin/users", { ...f, password: f.password || null }); toast.success("Team member added"); setF({ email: "", name: "", role: "staff", password: "" }); onAdded(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Add team member / allow-list a Google account" className="mb-6"><div className="grid md:grid-cols-5 gap-3 items-end">
      <F label="Email"><In value={f.email} onChange={set("email")} testid="team-email" /></F>
      <F label="Name"><In value={f.name} onChange={set("name")} testid="team-name" /></F>
      <F label="Role"><Sel value={f.role} onChange={set("role")} options={[{ value: "staff", label: "Staff (operations)" }, { value: "admin", label: "Admin (full access)" }]} testid="team-role" /></F>
      <F label="Password (optional)" hint="Min 10 chars. Leave empty for Google-only"><In type="password" value={f.password} onChange={set("password")} testid="team-password" /></F>
      <Btn onClick={add} disabled={!f.email || !f.name} data-testid="team-add-btn">Add</Btn>
    </div></Panel>
  );
};

export default function TeamAdmin() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);
  const isAdmin = user?.role === "admin";
  const patch = async (id, body, msg) => {
    try { await api.patch(`/admin/users/${id}`, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const resetPw = (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 10 characters)`);
    if (pw) patch(u.id, { password: pw }, "Password updated");
  };
  return (
    <div data-testid="admin-team">
      <PageHead eyebrow="System" title="Team & access" />
      <p className="text-sm text-zinc-500 mb-6 max-w-2xl">Only people listed here can sign in. Google sign-in is accepted only for active emails on this list. Staff manage enquiries, quotes and projects; admins can also edit content, pricing, templates and team access.</p>
      {isAdmin && <AddUser onAdded={load} />}
      {!users ? <Loading rows={1} /> : (
        <div className="overflow-x-auto border border-[#27272A]">
          <table className="w-full text-sm" data-testid="team-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-[#27272A]">{["Name", "Email", "Role", "Sign-in", "Added", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#27272A] last:border-0" data-testid={`team-row-${u.email}`}>
                  <td className="px-4 py-3">{u.name}</td>
                  <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                  <td className="px-4 py-3">{isAdmin && u.id !== user.id ? <Sel value={u.role} onChange={(v) => patch(u.id, { role: v }, "Role updated")} options={["staff", "admin"]} testid={`team-role-${u.email}`} /> : u.role}</td>
                  <td className="px-4 py-3 text-zinc-400">{u.has_password ? "Password + Google" : "Google only"}</td>
                  <td className="px-4 py-3 text-zinc-500">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3"><Pill status={u.active ? "active" : "disabled"} /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">{isAdmin && <>
                    <button onClick={() => resetPw(u)} className="text-xs text-zinc-400 hover:text-white mr-3" data-testid={`team-password-${u.email}`}>Set password</button>
                    {u.id !== user.id && <button onClick={() => patch(u.id, { active: !u.active }, u.active ? "Access revoked" : "Access restored")} className="text-xs text-[#00E5FF] hover:text-white" data-testid={`team-toggle-${u.email}`}>{u.active ? "Revoke" : "Restore"}</button>}
                  </>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
